import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FeedbackService } from '@app/core/services';
import { ApprovalLevelForEditModel } from '@app/models/system-settings/approval-level-for-edit.model';
import { ApprovalLevelModel } from '@app/models/system-settings/approval-level.model';
import { NoDataAvailableComponent } from '@app/shared/components/no-data-available/no-data-available.component';
import { DuplicateValueValidatorDirective } from '@app/shared/directives/validators/duplicate-value-validator.directive';
import { HasBegEndWhitespaceDirective } from '@app/shared/directives/validators/has-beg-end-whitespace.directive';
import { RequiredNoWhitespaceDirective } from '@app/shared/directives/validators/required-no-whitespace.directive';
import { sortAlphabetically } from '@app/utils';
import { ReadOnly } from '@app/utils/claims';
import { ApprovalUserParams } from '../approval-user/approval-user-params.model';
import { ApprovalUserComponent } from '../approval-user/approval-user.component';
import { ApprovalSetupService } from '../services/approval-setup-service';
@Component({
	selector: 'ss-approval-level',
	templateUrl: './approval-level.component.html',
	styleUrls: ['./approval-level.component.less'],
	standalone: true,
	imports: [
		FormsModule,
		DuplicateValueValidatorDirective,
		MatTableModule,
		MatSortModule,
		CdkDropList,
		NgIf,
		MatButtonModule,
		MatIconModule,
		CdkDragHandle,
		RequiredNoWhitespaceDirective,
		HasBegEndWhitespaceDirective,
		MatFormFieldModule,
		CdkDrag,
		NoDataAvailableComponent,
	],
})
export class ApprovalLevelComponent implements OnInit, AfterViewInit {
	@ViewChild('approvalLevelsForm') approvalLevelsForm: NgForm;
	@ViewChild(MatSort) sort: MatSort;
	@ViewChildren('focusRef') inputs: QueryList<ElementRef>;

	@Input()
	approvalType: number;

	@ReadOnly()
	isReadOnly = true;

	errors: any;
	newId = 0;
	columnsToDisplay: string[] = ['move', 'Description', 'addUsers', 'Final', 'remove'];
	dataSource = new MatTableDataSource<ApprovalLevelModel>();
	dragDisabled = true;
	originalData: string;
	isDataLoaded = false;

	constructor(
		private readonly approvalSetupService: ApprovalSetupService,
		private readonly feedbackService: FeedbackService,
		private readonly matDialog: MatDialog
	) {}

	ngOnInit(): void {
		this.dataSource = new MatTableDataSource();
		this.loadData();
	}

	ngAfterViewInit(): void {
		this.sort.direction = 'asc';
		this.dataSource.sort = this.sort;
	}

	drop(event: CdkDragDrop<string[]>): void {
		this.dragDisabled = true;
		moveItemInArray(this.dataSource.data, event.previousIndex, event.currentIndex);
		this.updateOrdinalAndFinal();
	}

	addLevel(): void {
		const newApproval = new ApprovalLevelModel();
		newApproval.Id = this.newId;
		newApproval.ApprovalTypeId = this.approvalType;
		this.newId--;
		this.dataSource.data = [newApproval, ...this.dataSource.data];
		this.updateOrdinalAndFinal();

		setTimeout((): void => {
			this.inputs.last.nativeElement.focus();
		}, 100);
	}

	deleteLevel(index: number): void {
		const itemToDelete = this.dataSource.data[index];
		this.dataSource.data.splice(index, 1);
		this.updateOrdinalAndFinal();
		//If the form controls aren't removed like this, the validator still thinks their values exist
		this.approvalLevelsForm.form.removeControl('description-' + itemToDelete.Id);
	}

	onCheckChange(approval: ApprovalLevelModel): void {
		if (this.isReadOnly) {
			return;
		}
		// Evaluate Final - only one can be marked as Final
		this.dataSource.data
			.filter((item) => item.Final === true && item.Id !== approval.Id)
			.forEach((al) => {
				al.Final = false;
			});
	}

	openUsersDialog(approval: ApprovalLevelModel): void {
		const dialogConfig: Partial<MatDialogConfig> = {};

		dialogConfig.disableClose = true;
		dialogConfig.maxHeight = '650px';
		dialogConfig.width = '500px';
		dialogConfig.data = {
			Id: approval.Id,
			Description: approval.Description,
			ApprovalType: this.approvalType,
		} as ApprovalUserParams;

		const dialogRef = this.matDialog.open(ApprovalUserComponent, dialogConfig);
		dialogRef.afterClosed().subscribe((result) => {
			if (result === undefined || this.isReadOnly) {
				return;
			}

			approval.ApprovalUsers = result;
			this.onSubmit();
		});
	}

	sortTable(columnName: string, sortDirection: boolean): void {
		const sortedList = sortAlphabetically(this.dataSource.data, columnName, !sortDirection);

		this.dataSource.data = sortedList;
		this.updateOrdinalAndFinal();
	}

	isFinalValid(): boolean {
		let isValid = true;
		const hasFinal = (element) => element.Final === true;
		if (this.dataSource.data.length > 0) {
			isValid = this.dataSource.data.some(hasFinal);
		}
		return isValid;
	}

	onSubmit(): void {
		if (!this.isFinalValid()) {
			this.feedbackService.warn('Save cannot continue unless an Approval Level is marked as "Final".');
			return;
		}

		const approvals = {
			ApprovalTypeEnum: this.approvalType,
			ApprovalLevels: this.dataSource.data.slice(),
		} as ApprovalLevelForEditModel;

		this.approvalSetupService.saveApprovals(approvals).subscribe(() => {
			this.errors = [];
			this.loadData();
			this.approvalLevelsForm.form.markAsPristine();
			this.approvalLevelsForm.form.markAsUntouched();
			this.feedbackService.success('saved', 'Approval Levels', { isPlural: true });
		});
	}

	onCancel(): void {
		this.dataSource.data = JSON.parse(this.originalData);
		this.approvalLevelsForm.form.markAsPristine();
		this.approvalLevelsForm.form.markAsUntouched();
	}

	private loadData(): void {
		this.isDataLoaded = false;
		if (this.approvalType > 0) {
			this.approvalSetupService.getList(this.approvalType).subscribe((data: ApprovalLevelModel[]) => {
				this.dataSource.data = data;
				this.isDataLoaded = true;
				this.originalData = JSON.stringify(data);
			});
		}
	}

	public canDelete(level: ApprovalLevelModel): boolean {
		return !(level.ApprovalUsers?.length > 0 && level.Id > 0) && !this.isReadOnly && !level.UsedOnPurchaseOrder;
	}

	public CanDeactivate(): boolean {
		return !this.approvalLevelsForm.dirty;
	}

	private updateOrdinalAndFinal() {
		for (let i = 0; i < this.dataSource.data.length; i++) {
			this.dataSource.data[i].Ordinal = i + 1;
			this.dataSource.data[i].Final = i === 0;
		}
		this.dataSource.sort = this.sort;
		this.approvalLevelsForm.form.markAsDirty();
	}

	reset(): void {
		this.loadData();
		this.sort.direction = 'asc';
		this.dataSource.sort = this.sort;
		this.approvalLevelsForm.form.markAsPristine();
		this.approvalLevelsForm.form.markAsUntouched();
	}
}
